import {ReactElement, useEffect, useState} from "react";

import {GetSinglePropertyDriversAndWorksheetItemsNoCalcEngineQuery, GetSinglePropertyDriversAndWorksheetItemsQuery, VersionType} from "../../../../../__generated__/generated_types";
import {TDriverMetricsYear} from "../../../../../contexts/account/data/logic/driversAndWorksheetData";
import {IFormulaBarUpdates} from "../../../../../contexts/account/data/useFormulaBarUpdates";
import {TAllAvailableDrivers} from "../../../../../contexts/account/data/utils";
import AnnualTargetValueGrowthDriverFxNode from "../components/formula-nodes/AnnualTargetValueGrowthDriverFxNode";
import MonthlyAverageGrowthDriverFxNode from "../components/formula-nodes/MonthlyAverageGrowthDriverFxNode";
import {OperationalDriverFxNode} from "../components/formula-nodes/OperationalDriverFxNode";
import PayrollCalculationFxNode from "../components/formula-nodes/PayrollCalculationFxNode";
import PercentGrowthDriverFxNode from "../components/formula-nodes/PercentGrowthDriverFxNode";
import RenovationsDriverFxNode, {TRenovationCost} from "../components/formula-nodes/RenovationsDriverFxNode";
import {RevenueDriverFxNode} from "../components/formula-nodes/RevenueDriverFxNode";
import WorksheetDriverFxNode, {TWorksheetLineItem} from "../components/formula-nodes/WorksheetDriverFxNode";

import {Property} from "../../../../../contexts/properties/logic";
import {AccountDriverFormulaManyNode} from "../components/formula-nodes/AccountDriverFormulaManyNode";
import {CustomDriverFxNode} from "../components/formula-nodes/CustomDriverFxNode";
import {OperationalMonthlyAverageDriverFxNode} from "../components/formula-nodes/OperationalMonthlyAverageDriverFxNode";
import YearlyAverageGrowthDriverFxNode from "../components/formula-nodes/YearlyAverageGrowthDriverFxNode";
import {ModelingMethodName} from "./utils";


interface IUseFormulaBarBuilderProps {
    accountId: string | undefined,
    // This is added for multi-year. For plan based budgeting, it will get replaced
    // by the global plan id.
    budgetYear: number,
    allAvailableDrivers: TAllAvailableDrivers,
    drivers: TDriverMetricsYear,
    isWorksheetDriven: boolean,
    year: number|undefined,
    version: VersionType.Reforecast|VersionType.Budget|undefined,
    rawData: GetSinglePropertyDriversAndWorksheetItemsQuery | GetSinglePropertyDriversAndWorksheetItemsNoCalcEngineQuery | undefined,
    readOnly: boolean,
    fbUpdates: IFormulaBarUpdates,
    worksheetTypeAhead?: string[] | undefined;
    isBulkUpdateBar?: boolean;
    disableCycleDetection?: boolean;
    isPropertyDriversUI?: boolean;
    editableFxBarChecker?: (modelingMethod: ModelingMethodName, modelingMethodLocked: boolean) => boolean;
    selectedAccountIds: string[];
    currentPropertyId?: string | undefined;
    property: Property | undefined;
}

export interface IUseFormulaBarBuilderReturn {
    nodes: ReactElement[],
}

export default function useFormulaBarBuilder(props: IUseFormulaBarBuilderProps): IUseFormulaBarBuilderReturn {

    const [nodes, setNodes] = useState<ReactElement[]>([]);

    const getIsLocked = (modelingMethod: ModelingMethodName, modelingMethodLocked: boolean): boolean => {
        if (modelingMethod == "account" && props.isPropertyDriversUI && props.selectedAccountIds.length > 1 && !props.isBulkUpdateBar) {
            return true;
        }
        // If editableFxBarChecker was passed down, then evaluate against it
        if (props.editableFxBarChecker) {
            return props.readOnly || !(props.editableFxBarChecker(modelingMethod, modelingMethodLocked));
        }

        return props.readOnly;
    };

    useEffect(
            () => {
                if(!props.version || !props.year || !props.drivers || !props.fbUpdates.isReady){
                    return;
                }

                const builtNodes: ReactElement[] = [];

                const { drivers } = props;
                if(drivers){

                    const lastDriverType = getLastDriverType(drivers);

                    let nodeIdx = 0;

                    const accounts: {
                        name: string,
                        id: string,
                        lookbackPeriod: number|null|undefined,
                    }[] = drivers.account.map(driver => ({
                        name: driver.glName,
                        id: driver.accountId,
                        lookbackPeriod: driver.lookbackPeriod,
                    }));

                    if (accounts.length > 0) {
                        builtNodes.push(
                                <AccountDriverFormulaManyNode
                                        driverAccounts={accounts}
                                        driverAugments={drivers.accountPercentageAugment}
                                        locked={getIsLocked("account", false)} // We decided to not implement all or nothing locking for account drivers due to a need to still be able to lock individual source accounts.
                                        nodeId={`driver_node`}
                                        key={nodeIdx++}
                                        isLastDriverType={lastDriverType === 'account'}
                                        isBulkUpdateBar={props.isBulkUpdateBar}
                                        disableCycleDetection={props.disableCycleDetection}
                                        isPropertyDriversUI={props.isPropertyDriversUI}
                                        fbUpdates={props.fbUpdates}
                                        editableFxBarChecker={props.editableFxBarChecker}
                                        selectedAccountIds={props.selectedAccountIds}
                                        accountId={props.accountId ?? ""}
                                        budgetYear={props.budgetYear}
                                        versionType={props.version}
                                        currentPropertyId={props.currentPropertyId ?? ""}
                                />
                        );
                    }

                    // Operational drivers
                    drivers.operational.forEach((driver, idx, arr) => {
                        builtNodes.push(
                                <OperationalDriverFxNode
                                        driverAccount={{
                                            metricType: driver.type,
                                            secondaryType: driver.secondaryType,
                                            id: driver.driverMetricTypeId,
                                            lookbackPeriod: driver.lookbackPeriod,
                                            name: driver.name,
                                        }}
                                        locked={getIsLocked("operational", driver.locked || driver.valuesLocked)}
                                        canDelete={!getIsLocked("operational", driver.locked || driver.valuesLocked || driver.configLocked)}
                                        nodeId={`operational_node_${idx}`}
                                        key={nodeIdx++}
                                        isLastDriverType={lastDriverType === 'operational' && idx == arr.length - 1}
                                        isBulkUpdateBar={props.isBulkUpdateBar}
                                        isPropertyDriversUI={props.isPropertyDriversUI}
                                        fbUpdates={props.fbUpdates}
                                        editableFxBarChecker={props.editableFxBarChecker}
                                        selectedAccountIds={props.selectedAccountIds}
                                        existingDrivers={drivers.operational}
                                        driver={driver}
                                />
                        );
                    });

                    if(drivers.operationalMonthlyAverage) {
                        const driver = drivers.operationalMonthlyAverage;
                        builtNodes.push(
                            <OperationalMonthlyAverageDriverFxNode
                                nodeId={"operational_monthly_average_node"}
                                property={props.property}
                                locked={getIsLocked("operationalMonthlyAverage", driver.locked)}
                                isLastDriverType={lastDriverType === "operationalMonthlyAverage"}
                                fbUpdates={props.fbUpdates}
                                editableFxBarChecker={props.editableFxBarChecker}
                                selectedAccountIds={props.selectedAccountIds}
                                driver={driver}
                            />
                        );
                    }

                    // Revenue drivers
                    drivers.revenue.forEach((driver, idx, arr) => {
                        builtNodes.push(
                                <RevenueDriverFxNode
                                        revenueType={
                                            { revenueTypeKey: driver.revenueType, id: driver.sourceId }
                                        }
                                        nodeId={`revenue_node_${idx}`}
                                        locked={getIsLocked("revenue", driver.locked)}
                                        key={nodeIdx++}
                                        isLastDriverType={lastDriverType === 'revenue' && idx == arr.length - 1}
                                        fbUpdates={props.fbUpdates}
                                />
                        );
                    });

                    // Payroll drivers
                    // DEV: Haven > Hidden Acres > Payroll- Maintenance Technician(6352)
                    // DEV: http://localhost:3000/account/43a2b697-81d9-464f-9092-86df90a29a92/budget
                    if(drivers.payroll.length > 0){
                        drivers.payroll.forEach((each, i, arr) => {
                                builtNodes.push(
                                    <PayrollCalculationFxNode
                                            allDrivers={props.allAvailableDrivers}
                                            driver={each}
                                            nodeId={`payroll_calcs_node`}
                                            locked={getIsLocked("payroll", false)} // payroll is never locked as all or nothing. rather individual positions are locked. hence the second argument is false.
                                            getIsLocked={(positionLocked: boolean) => getIsLocked("payroll", positionLocked)} // this controls the disabled state of individual positions.
                                            isLastDriverType={lastDriverType === 'payroll'}
                                            isLastDriver={i === arr.length - 1}
                                            isWorksheetDriven={props.isWorksheetDriven}
                                            isBulkUpdateBar={props.isBulkUpdateBar}
                                            isPropertyDriversUI={props.isPropertyDriversUI}
                                            key={`payroll_driver_${each.itemType}`}
                                            fbUpdates={props.fbUpdates}
                                    />
                                );
                            }
                        );
                    }

                    // Renovation drivers
                    if(drivers.renovations.length > 0){
                        const renoCosts: TRenovationCost[] = drivers.renovations.map(renoCost => {
                            return {
                                name: renoCost.name,
                                id: renoCost.id,
                            };
                        });
                        builtNodes.push(
                                <RenovationsDriverFxNode
                                        renovationCosts={renoCosts}
                                        nodeId={`renovations_node`}
                                        locked={getIsLocked("renovations", true)} // TODO: Remove this once we have the locked value from the API. TODO: Implement lock handling in the RenovationsDriverFxNode.
                                        key={nodeIdx++}
                                        isLastDriverType={lastDriverType === 'renovations'}
                                        fbUpdates={props.fbUpdates}
                                        budgetYear={props.budgetYear}
                                />
                        );
                    }

                    // Monthly Average growth drivers
                    if (drivers.monthlyAverage.length > 0) {
                        const driver = drivers.monthlyAverage[0];
                        if (driver && props.property) {
                            builtNodes.push(
                                <MonthlyAverageGrowthDriverFxNode
                                    driver={driver}
                                    nodeId="monthly_average_growth_node"
                                    locked={getIsLocked("monthlyAverage", driver.locked)}
                                    key={nodeIdx++}
                                    isLastDriverType={lastDriverType === 'monthlyAverage'}
                                    fbUpdates={props.fbUpdates}
                                    property={props.property}
                                />
                            );
                        }
                    }

                    // Yearly Average growth drivers
                    if (drivers.yearlyAverage.length > 0) {
                        const driver = drivers.yearlyAverage[0];
                        if (driver && props.property) {
                            builtNodes.push(
                                <YearlyAverageGrowthDriverFxNode
                                    driver={driver}
                                    nodeId="yearly_average_growth_node"
                                    locked={getIsLocked("yearlyAverage", driver.locked)}
                                    key={nodeIdx++}
                                    isLastDriverType={lastDriverType === 'yearlyAverage'}
                                    fbUpdates={props.fbUpdates}
                                    property={props.property}
                                />
                            );
                        }
                    }

                    // % Growth drivers
                    if (drivers.percentGrowth.length > 0) {
                        const driver = drivers.percentGrowth[0];
                        if(driver){
                            builtNodes.push(
                                <PercentGrowthDriverFxNode
                                    driver={driver}
                                    nodeId="percent_growth_node"
                                    locked={getIsLocked("percentGrowth", driver.locked)}
                                    key={nodeIdx++}
                                    isLastDriverType={lastDriverType === 'percentGrowth'}
                                    fbUpdates={props.fbUpdates}
                                    versionType={props.version}
                                />
                            );
                        }
                    }

                    // Annual Target Value growth drivers
                    if (drivers.annualTargetValue.length > 0) {
                        const driver = drivers.annualTargetValue[0];
                        if(driver){
                            builtNodes.push(
                                <AnnualTargetValueGrowthDriverFxNode
                                    driver={driver}
                                    nodeId="annual_target_value_growth_node"
                                    locked={getIsLocked("annualTargetValue", driver.locked)}
                                    key={nodeIdx++}
                                    isLastDriverType={lastDriverType === 'annualTargetValue'}
                                    fbUpdates={props.fbUpdates}
                                />
                            );
                        }
                    }

                    // Worksheet drivers
                    /**
                     * In a dirty state where the user has deleted a worksheet, we don't want an empty (Line items: ) node to show
                     */
                    if(props.fbUpdates.isUserRequestingWorksheetDeletion === false && (drivers.worksheet.length > 0 || props.isWorksheetDriven)){
                        const lineItems: TWorksheetLineItem[] = drivers.worksheet.map(lineItem => {
                            return {
                                name: lineItem.description,
                                locked: lineItem.locked || lineItem.valuesLocked
                            };
                        });
                        builtNodes.push(
                                <WorksheetDriverFxNode
                                        accountId={props.accountId}
                                        budgetYear={props.budgetYear}
                                        lineItems={lineItems}
                                        nodeId={'worksheet_node'}
                                        locked={getIsLocked("worksheet", false)} // worksheet is never locked as all or nothing. rather individual line items are locked. hence the second argument is false.
                                        getIsLocked={(lineItemLocked: boolean) => getIsLocked("worksheet", lineItemLocked)} // this controls the disabled state of individual line items.
                                        key={nodeIdx++}
                                        isLastDriverType={lastDriverType === 'worksheet'}
                                        isWorksheetDriven={props.isWorksheetDriven}
                                        isBulkUpdateBar={props.isBulkUpdateBar}
                                        isPropertyDriversUI={props.isPropertyDriversUI}
                                        fbUpdates={props.fbUpdates}
                                        worksheetTypeAhead={props.worksheetTypeAhead}
                                />
                        );
                    }

                    // Custom Drivers
                    drivers.customDriver.forEach((driver, idx, arr) => {
                        builtNodes.push(
                                <CustomDriverFxNode
                                        id={driver.id}
                                        itemName={driver.itemName}
                                        locked={getIsLocked("customDriver", driver.locked)}
                                        nodeId={`custom_driver_node_${idx}`}
                                        key={nodeIdx++}
                                        isLastDriverType={lastDriverType === 'customDriver' && idx == arr.length - 1}
                                        isBulkUpdateBar={props.isBulkUpdateBar}
                                        isPropertyDriversUI={props.isPropertyDriversUI}
                                        fbUpdates={props.fbUpdates}
                                        editableFxBarChecker={props.editableFxBarChecker}
                                        selectedAccountIds={props.selectedAccountIds}
                                />
                        );
                    });
                }

                setNodes(builtNodes);
            },
            [props.fbUpdates.isReady, props.fbUpdates.options, props.version, props.year, props.drivers, props.property?.reforecastStartMonthIndex, props.budgetYear, props.worksheetTypeAhead, props.allAvailableDrivers?.payroll]
    );

    return { nodes };
}

type TLastDriverType = 'account'|'operational'|'revenue'|'payroll'|'renovations'|'worksheet'|'monthlyAverage'|'yearlyAverage'|'percentGrowth'|'annualTargetValue'|'customDriver'|'operationalMonthlyAverage'|undefined;

const getLastDriverType = (drivers:TDriverMetricsYear):TLastDriverType => {
    let isLast:TLastDriverType;
    if(drivers.account.length > 0){
        isLast = 'account';
    }
    if(drivers.operational.length > 0){
        isLast = 'operational';
    }
    if(drivers.revenue.length > 0){
        isLast = 'revenue';
    }
    if(drivers.payroll.length > 0){
        isLast = 'payroll';
    }
    if(drivers.renovations.length > 0){
        isLast = 'renovations';
    }
    if(drivers.worksheet.length > 0){
        isLast = 'worksheet';
    }
    if(drivers.monthlyAverage.length > 0){
        isLast = 'monthlyAverage';
    }
    if(drivers.yearlyAverage.length > 0){
        isLast = 'yearlyAverage';
    }
    if(drivers.percentGrowth.length > 0){
        isLast = 'percentGrowth';
    }
    if(drivers.annualTargetValue.length > 0){
        isLast = 'annualTargetValue';
    }
    if(drivers.customDriver.length > 0){
        isLast = 'customDriver';
    }
    if(drivers.operationalMonthlyAverage){
        isLast = 'operationalMonthlyAverage';
    }
    return isLast;
};
